import { SearchOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Divider, Form, Input, message, Row, Select } from 'antd';
import moment from 'moment';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { IListColumn, ISortColumn, PaginationResponse } from 'src/@types/search-params';
import { getAllPackageAssigned, getAllPackages } from 'src/api/package_assigned';
import CGridView from 'src/components/CGridView';
import TableContent from 'src/components/CTable';
import useWindowSize from 'src/hooks/useWindowSize';
import { useAppSelector } from 'src/redux/hook';
import { setGlobalState } from 'src/redux/slice/globalSlice';
import { orderStatus } from 'src/shared/common/package_assigned';
import { SimType } from 'src/shared/enum/sim.enum';
import { IOrderStatus, IPackageAssigned } from 'src/shared/interface/package_assigned';
import {
  exportExcel,
  formatDateTime,
  formatMoney,
  nonAccentVietnamese,
  parseQueryString,
  renderSubType,
} from 'src/utils';
import { simTypeToText } from 'src/utils/sim.enum';
import { usePermission } from 'src/hooks/usePermission';

function OrderListPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [_, setSearchParams] = useSearchParams();
  const location = useLocation();
  const params = location.search;
  const merchantInfo: string | null = localStorage.getItem('merchantInfo');
  const { openTabs, masterData, activeKeyId } = useAppSelector((state) => state.global);

  const size = useWindowSize();
  const [data, setData] = useState<PaginationResponse<IPackageAssigned>>();
  const [sortColumn, setSortColumn] = useState<ISortColumn>();
  const [dataPackage, setDataPackage] = useState<IOrderStatus[]>([]);

  const { PERMISSIONS, hasPermission } = usePermission();

  const fetchGetPackageAssigned = async () => {
    await getAllPackages().then((res: any) => {
      if (res) {
        const dataPackage: IOrderStatus[] = [{ value: '', label: 'Tất cả' }];
        res?.map((item: any) => {
          dataPackage?.push({
            label: item.mbfName,
            value: item.mbfCode,
          });
        });
        setDataPackage(dataPackage);
      }
    });
  };

  function handleExport() {
    const link = document.createElement('a');
    link.target = '_blank';
    link.download = `danh_sach_kitting_${moment().format('DDMMYYYYHHmmss')}.xlsx`;
    exportExcel(process.env.REACT_APP_API_URL + '/api/Provider/register-package/export' + params)
      .then((res) => {
        link.href = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.ms-excel' }));
        link.click();
      })
      .catch((error) => {
        if (error.status === 403) {
          message.error(`Tài khoản của bạn không có quyền truy cập vào chức năng này`);
        }
      });
  }

  function handleFormSearch(value: any) {
    let values = { ...value };
    values = {
      ...values,
      id: values.id?.trim(),
      dateFrom:
        value.dateRange && value?.dateRange[0]
          ? value?.dateRange[0].format('YYYY-MM-DD')
          : undefined,
      dateTo:
        value.dateRange && value?.dateRange[1]
          ? value?.dateRange[1].format('YYYY-MM-DD')
          : undefined,
      dateRange: undefined,
      pageIndex: 1,
      pageSize: 10,
    };
    Object.keys(values).forEach((key) => {
      if (!values[key]) {
        delete values[key];
      }
    });
    fetchListPackageAssigned(values);
    setSearchParams(values);
  }

  function openCreateOrderPage() {
    dispatch(
      setGlobalState({
        activeKeyMenu: 'order-assigned-package/create',
      }),
    );
    if (!openTabs.find((tab) => tab.key === 'order-assigned-package/create')) {
      dispatch(
        setGlobalState({
          openTabs: [
            ...openTabs,
            {
              label: 'Tạo mới đơn hàng gán gói',
              children: '',
              key: 'order-assigned-package/create',
              closable: true,
            },
          ],
        }),
      );
    }
    dispatch(setGlobalState({ order: undefined }));
    navigate(`/management/order-assigned-package/create`);
  }

  const handleDetailOrder = (order: any) => {
    dispatch(
      setGlobalState({
        activeKeyMenu: 'order-assigned-package/detail' + '?id=' + order.id,
      }),
    );
    if (!openTabs.find((tab) => tab.key === 'order-assigned-package/detail' + '?id=' + order.id)) {
      dispatch(
        setGlobalState({
          openTabs: [
            ...openTabs,
            {
              label: 'Xem chi tiết gán gói ' + order.id,
              children: '',
              key: 'order-assigned-package/detail' + '?id=' + order.id,
              closable: true,
            },
          ],
        }),
      );
    }
    navigate(`/management/order-assigned-package/detail?id=${order.id}`);
  };

  const columns: IListColumn[] = [
    { name: 'STT', fieldName: 'index', width: 20, isHideSort: true },
    {
      name: 'Mã đơn',
      fieldName: 'id',
      renderSpecialCard: (order: IPackageAssigned) => (
        <button
          disabled={!hasPermission(PERMISSIONS.ASSIGN_PACKAGE.VIEW_DETAIL)}
          className={`underline md:whitespace-nowrap cursor-pointer bg-transparent border-transparent ${
            !hasPermission(PERMISSIONS.ASSIGN_PACKAGE.VIEW_DETAIL)
              ? 'text-gray-400'
              : 'text-blue-400'
          } font-semibold text-right`}
          onClick={() => handleDetailOrder(order)}
        >
          {order.id ?? ''}
        </button>
      ),
    },
    {
      name: 'Loại SIM',
      fieldName: 'simType',
      renderSpecialCard: (order: IPackageAssigned) => <span>{simTypeToText(order?.simType)}</span>,
    },
    {
      name: 'Loại thuê bao',
      fieldName: 'subType',
      isHideSort: true,
      renderSpecialCard: (record) => {
        return <p>{renderSubType(masterData, record?.subType)}</p>;
      },
    },
    {
      name: 'Mã gói',
      fieldName: 'mbfCode',
      isHideSort: false,
    },
    {
      name: 'Tên gói',
      fieldName: 'mbfName',
      isHideSort: true,
    },
    {
      name: 'Trạng thái',
      fieldName: 'statusString',
      renderSpecialCard: (record: IPackageAssigned) => {
        const status = orderStatus.find((item) => item.value === record.statusString);
        return <span className='whitespace-nowrap'>{status?.label ?? ''}</span>;
      },
    },
    {
      name: 'Số giao dịch thanh toán',
      fieldName: 'transactionId',
    },
    {
      name: 'Thành tiền',
      fieldName: 'totalAmount',
      isHideSort: true,
      renderSpecialCard: (record: IPackageAssigned) => {
        return <span>{record.toTalAmount ? formatMoney(record.toTalAmount, 'đ') : 0}</span>;
      },
    },
    {
      name: 'Tổng cộng',
      fieldName: 'countSerial',
      isHideSort: true,
      width: '5%',
    },
    {
      name: 'Thành công',
      fieldName: 'quantitySuccess',
      isHideSort: true,
      width: '5%',
    },
    {
      name: 'Thất bại',
      fieldName: 'quantityFailed',
      isHideSort: true,
      width: '5%',
    },
    {
      name: 'Người tạo',
      fieldName: 'createdBy',
    },
    {
      name: 'Ngày tạo',
      fieldName: 'createdAt',
      renderSpecialCard: (record: IPackageAssigned) => {
        return <span>{record.createdAt ? formatDateTime(record.createdAt) : ''}</span>;
      },
    },
    {
      name: 'Ngày cập nhật cuối',
      fieldName: 'updatedAt',
      renderSpecialCard: (record: IPackageAssigned) => {
        return (
          <span>
            {record.updatedAt &&
            record.createdAt &&
            new Date(record.updatedAt).getTime() === new Date(record.createdAt).getTime()
              ? ''
              : record.updatedAt
              ? formatDateTime(record.updatedAt)
              : ''}
          </span>
        );
      },
    },
    {
      name: 'Người cập nhật cuối',
      fieldName: 'updatedBy',
    },
    {
      name: 'Thao tác',
      fieldName: 'action',
      renderSpecialCard: (order) => (
        <button
          disabled={!hasPermission(PERMISSIONS.ASSIGN_PACKAGE.VIEW_DETAIL)}
          className={`underline whitespace-nowrap cursor-pointer bg-transparent border-transparent ${
            !hasPermission(PERMISSIONS.ASSIGN_PACKAGE.VIEW_DETAIL)
              ? 'text-gray-400'
              : 'text-blue-400'
          } font-semibold`}
          onClick={() => handleDetailOrder(order)}
        >
          Xem chi tiết
        </button>
      ),
      isHideSort: true,
    },
  ];

  const fetchListPackageAssigned = async (params = {}) => {
    const res = await getAllPackageAssigned(params);
    if (res) {
      setData(res);
    }
  };

  useEffect(() => {
    fetchGetPackageAssigned();
    fetchListPackageAssigned();
    setSortColumn(undefined);
    form.resetFields();
  }, [activeKeyId]);

  const renderOptionSubType = () => {
    const listOption = [
      {
        value: '',
        label: 'Tất cả',
      },
    ];
    if (masterData?.subType && masterData?.subType?.length > 0) {
      masterData?.subType?.map((item: any) => {
        listOption.push({
          label: item?.name,
          value: item?.description,
        });
      });
    }
    return listOption;
  };

  return (
    <Fragment>
      <Form onFinish={handleFormSearch} layout={size.width < 768 ? 'vertical' : 'inline'}>
        <div className='md:flex md:gap-4 md:flex-wrap'>
          <Form.Item name='id' labelCol={{ span: 24 }}>
            <Input prefix={<SearchOutlined />} placeholder='Mã đơn' />
          </Form.Item>
          <Form.Item name='simType' label='Loại Sim'>
            <Select
              showSearch
              className='min-w-[150px]'
              placeholder='Vui lòng chọn'
              defaultValue={''}
              options={[
                {
                  value: '',
                  label: 'Tất cả',
                },
                {
                  value: SimType.PHYSICAL_SIM,
                  label: simTypeToText(SimType.PHYSICAL_SIM),
                },
                {
                  value: SimType.ESIM,
                  label: simTypeToText(SimType.ESIM),
                },
              ]}
            ></Select>
          </Form.Item>
          <Form.Item name='subType' label='Loại thuê bao'>
            <Select
              showSearch
              className='min-w-[200px]'
              placeholder='Vui lòng chọn'
              defaultValue={''}
              options={renderOptionSubType()}
              filterOption={(input, option) => {
                return nonAccentVietnamese(
                  (option!.label as unknown as string).toString()?.toLowerCase(),
                ).includes(nonAccentVietnamese(input?.toLowerCase()?.trim()));
              }}
            ></Select>
          </Form.Item>
          <Form.Item name='mbfCode' label='Gói'>
            <Select
              showSearch
              className='min-w-[150px]'
              placeholder='Vui lòng chọn'
              defaultValue={''}
              options={dataPackage}
              filterOption={(input, option) => {
                return nonAccentVietnamese(
                  (option!.label as unknown as string).toString()?.toLowerCase(),
                ).includes(nonAccentVietnamese(input?.toLowerCase()?.trim()));
              }}
            ></Select>
          </Form.Item>
          <Form.Item name='status' label='Trạng thái'>
            <Select
              showSearch
              className='min-w-[160px] w-full'
              placeholder='Vui lòng chọn'
              defaultValue={''}
              options={orderStatus}
            ></Select>
          </Form.Item>
          <Form.Item name='' label='Người tạo'>
            <Select
              showSearch
              className='min-w-[150px]'
              defaultValue={merchantInfo ? JSON.parse(merchantInfo).merchantName : ''}
            >
              {/* <Select.Option value='merchantName'>Tất cả</Select.Option> */}
              <Select.Option value='merchantName'>
                {merchantInfo ? JSON.parse(merchantInfo).merchantName : ''}
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name='dateRange' label='Ngày tạo'>
            <DatePicker.RangePicker
              disabledDate={(current) => current && current >= moment().endOf('day')}
              placeholder={['từ', 'đến']}
              format={'DD/MM/YYYY'}
            />
          </Form.Item>
        </div>

        <div className='row-button flex-wrap md:flex-nowrap md:justify-end gap-y-4 my-6'>
          <Button type='primary' htmlType='submit'>
            Tìm kiếm
          </Button>
          {hasPermission(PERMISSIONS.ASSIGN_PACKAGE.CREATE) && (
            <Button onClick={openCreateOrderPage}>Tạo mới</Button>
          )}
          {hasPermission(PERMISSIONS.ASSIGN_PACKAGE.EXPORT) && (
            <Button onClick={handleExport}>Export</Button>
          )}
        </div>
      </Form>
      <div className='mb-12 lg:mb-0 '>
        {size.width < 1024 ? (
          <CGridView
            length={data?.results.length}
            totalRecord={data?.rowCount ?? 0}
            columns={columns}
            title={() => <div className='text-xl font-bold mb-6'>Danh sách đơn gán gói</div>}
          >
            {data?.results && data?.results.length > 0 ? (
              data?.results.map((item: IPackageAssigned) => {
                return (
                  <div key={'data-row-' + item.id}>
                    <Divider className='border-t-black' />

                    <Row className='mb-6 items-center' gutter={[4, 16]}>
                      {columns.map((ele) => {
                        if (ele.fieldName !== 'index' && ele.fieldName !== 'action') {
                          return (
                            <>
                              <Col span={11} className={`font-bold label`}>
                                <div className='flex gap-2 items-center'>{ele.name}</div>
                              </Col>
                              <Col className='break-words text-right' span={13}>
                                {ele?.renderSpecialCard
                                  ? ele?.renderSpecialCard(item, null, null, null)
                                  : item[`${ele?.fieldName}`]}
                              </Col>
                            </>
                          );
                        } else if (ele.fieldName === 'action') {
                          return (
                            <Col key={'column-' + item.id + ele.fieldName} span={24}>
                              <div className='flex gap-6'>
                                {ele?.renderSpecialCard
                                  ? ele?.renderSpecialCard(item, null, null, null)
                                  : item[`${ele?.fieldName}`]}
                              </div>
                            </Col>
                          );
                        } else {
                          return <></>;
                        }
                      })}
                    </Row>
                  </div>
                );
              })
            ) : (
              <p>{'Không có dữ liệu'}</p>
            )}
          </CGridView>
        ) : (
          <TableContent
            bordered
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'Không tìm thấy kết quả' }}
            dataSource={data?.results ?? []}
            columns={columns}
            totalRecord={data?.rowCount ?? 0}
            sortColumn={sortColumn}
            onPageChange={(pageNumber, pageSize) => {
              const pagination = parseQueryString(location.search);
              fetchListPackageAssigned({
                ...pagination,
                pageIndex: pageNumber ?? 1,
                pageSize: pageSize ?? 10,
                sortField: pagination.sortField ?? undefined,
                asc: pagination.asc ?? undefined,
              });
            }}
            onSort={(sort: ISortColumn) => {
              const pagination = parseQueryString(location.search);
              fetchListPackageAssigned({
                ...pagination,
                pageIndex: pagination.pageIndex ?? 1,
                pageSize: pagination.pageSize ?? 10,
                sortField: sort.fieldName,
                asc:
                  sort.orderType === 'asc' ? true : sort.orderType === 'desc' ? 'false' : undefined,
              });
            }}
          />
        )}
      </div>
    </Fragment>
  );
}

export default OrderListPage;
